import { useEffect, useState } from 'react'
import { FacturaAPI } from '../../../../../../api/facturas.api'
import { useGlobalContext } from '../../../../../../context/GlobalState'
import { getErrorMessage } from '../utils/utils'
import { sileo } from 'sileo'

const CredentialForm = ({
  embedded = false,
  formId,
  title = 'Crear credencial',
  description,
  submitLabel = 'Crear credencial',
  onSuccess,
  secondaryAction = null
}) => {
  const facturasAPI = new FacturaAPI()
  const { currentBusiness, setCurrentBusiness } = useGlobalContext()
  const [form, setForm] = useState({
    user_rut: '',
    password: '',
    digital_certificate_password: '',
    master_entity_id: '',
    credential_type_id: 70
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onChange = e => setForm({ ...form, [e.target.name]: e.target.value })
  const toInt = value =>
    value === '' || value === null || value === undefined ? undefined : Number(value)

  const validate = () => {
    if (!form.user_rut) return 'Ingresa el RUT (sin puntos y con guión)'
    if (!/^[0-9]+-[0-9kK]$/.test(form.user_rut)) return 'RUT inválido. Formato esperado: 12345678-9'
    if (!form.password) return 'Ingresa la contraseña del SII'
    if (!form.digital_certificate_password) {
      return 'Ingresa la contraseña del certificado digital'
    }

    return null
  }

  const handleSubmit = async e => {
    e.preventDefault()

    const msg = validate()
    if (msg) {
      sileo.error({ title: 'Error de validación', description: msg })
      return
    }

    const payload = {
      user_rut: String(form.user_rut).trim(),
      password: form.password,
      digital_certificate_password: form.digital_certificate_password,
      master_entity_id: toInt(form.master_entity_id),
      credential_type_id:
        form.credential_type_id === '' ? undefined : toInt(form.credential_type_id)
    }

    setIsSubmitting(true)

    try {
      const response = await facturasAPI.crearCredenciales(currentBusiness.id, payload)

      setCurrentBusiness({
        ...currentBusiness,
        tokenFacturas: response?.data?.response_tupana?.status === 'VALID'
      })

      if (onSuccess) {
        onSuccess(response)
      }
    } catch (err) {
      console.log('Error al emitir la factura:', err)
      sileo.error({
        title: 'Error de credencial',
        description: getErrorMessage(err, 'Error al crear la credencial')
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    const masterEntityId = async () => {
      try {
        const response = await facturasAPI.getSearchEntityRut(currentBusiness?.rut)
        setForm(prev => ({ ...prev, master_entity_id: response?.data?.id }))
      } catch (err) {
        console.error(err)
        sileo.error({ title: 'Error de búsqueda', description: 'Error al buscar la entidad' })
      }
    }

    if (currentBusiness?.rut) {
      masterEntityId()
    }
  }, [currentBusiness?.rut])

  return (
    <div className={embedded ? 'w-full' : 'flex w-full justify-center bg-gray-50 p-6'}>
      <form
        id={formId}
        onSubmit={handleSubmit}
        className={`w-full ${embedded ? 'space-y-5' : 'max-w-3xl space-y-6'}`}
      >
        <header className='space-y-2'>
          <h1 className='text-2xl font-bold'>{title}</h1>
          {description && <p className='text-sm leading-6 text-slate-600'>{description}</p>}
          <p className='hint'>
            {currentBusiness?.name} {currentBusiness?.rut}
          </p>
        </header>

        <section
          className={`flex flex-col gap-4 bg-white ${
            embedded
              ? 'rounded-[24px] border border-slate-200 p-5 shadow-sm'
              : 'rounded-2xl p-4 shadow'
          }`}
        >
          <div>
            <label className='label'>RUT del usuario autorizado en el SII</label>
            <input
              name='user_rut'
              placeholder='12345678-9'
              className='input'
              value={form.user_rut}
              onChange={onChange}
            />
            <p className='hint'>Sin puntos y con guión</p>
          </div>

          <div>
            <label className='label'>Contraseña del usuario autorizado en el SII</label>
            <input
              name='password'
              type='password'
              className='input'
              value={form.password}
              onChange={onChange}
              autoComplete='new-password'
            />
          </div>

          <div>
            <label className='label'>Contraseña del certificado digital o firma electrónica</label>
            <input
              name='digital_certificate_password'
              type='password'
              className='input'
              value={form.digital_certificate_password}
              onChange={onChange}
              autoComplete='new-password'
            />
          </div>
        </section>

        <section
          className={
            secondaryAction
              ? 'flex flex-col items-center justify-center gap-4 pt-2 sm:flex-row sm:gap-6'
              : 'flex items-center gap-3'
          }
        >
          {secondaryAction && (
            <button
              type='button'
              onClick={secondaryAction.onClick}
              className='inline-flex w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-sans font-semibold text-primary shadow-sm transition hover:bg-slate-50 sm:w-auto sm:min-w-[180px]'
            >
              {secondaryAction.label}
            </button>
          )}

          <button
            className={
              secondaryAction
                ? 'inline-flex w-full items-center justify-center rounded-xl bg-primary px-6 py-3 text-sm font-sans font-semibold text-white shadow-md transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:min-w-[180px]'
                : 'btn'
            }
            type='submit'
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creando...' : submitLabel}
          </button>
        </section>

        <style>{`
          .label { display:block; font-size:.9rem; margin-bottom:.2rem; }
          .hint { font-size: .75rem; color: #6b7280; margin-top:.25rem; }
          .input { width: 100%; padding: 0.65rem 0.85rem; border: 1px solid #e5e7eb; border-radius: 0.9rem; outline: none; }
          .input:focus { box-shadow: 0 0 0 2px rgba(59,130,246,.3); border-color: #93c5fd; }
          .btn { padding: 0.65rem 1.5rem; border-radius: 9999px; background: #111827; color: white; }
          .btn:disabled { opacity: .6; }
        `}</style>
      </form>
    </div>
  )
}

export default CredentialForm
